// HV-TaskFlow V1.0.46 — Fix React #310 (useWatch must be in component, not IIFE)
// Build marker: 2026-05-23-fix-310-v2
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Progress,
  Button,
  Avatar,
  Tag,
  Tabs,
  Input,
  InputNumber,
  Divider,
  Checkbox,
  Space,
  Spin,
  Result,
  Dropdown,
  Modal,
  App as AntdApp,
  Form,
  Select,
  DatePicker,
  Empty,
  Timeline,
  Segmented,
  Tooltip,
} from 'antd';
import {
  UpdateProgressModal,
  SubmitForReviewModal,
  SendBackModal,
  ApproveTaskModal,
  TaskAttachmentsPanel,
} from '@/components/task/TaskActionModals';
import { KpiBreakdownModal } from '@/components/task/KpiBreakdownModal';
import { StarLevelRate, URGENT_TOOLTIPS, IMPORTANT_TOOLTIPS, getLevelColor } from '@/components/common/StarLevelRate';  // V1.0.52
import { printService } from '@/services/attachmentService';
import {
  MoreHorizontal,
  ArrowLeft,
  Plus,
  Edit2,
  Copy as CopyIcon,
  XCircle,
  History,
  CheckCircle2,
  Printer,
  Download,
  AlertTriangle,
  Play,
  Trash2,
  CornerUpLeft,
  Lock,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { taskService } from '@/services/taskService';
import { userService } from '@/services/userService';
import { TaskStatusBadge, PriorityBadge } from '@/components/common/Badges';
import { Card } from '@/components/common/PageHeader';
import { RichEditor, RichContent, type RichEditorValue } from '@/components/common/RichEditor';
import { parseDescription, serializeDescription, sanitizeHtml, isEmptyHtml } from '@/utils/richContent';
import { useAuthStore } from '@/store/authStore';

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message, modal } = AntdApp.useApp();
  const { user: currentUser } = useAuthStore();

  const [newComment, setNewComment] = useState<RichEditorValue>({ html: '', attachments: [] });
  const [mentionIds, setMentionIds] = useState<string[]>([]);  // V1.0.63i: tag người trong comment
  const [progressOpen, setProgressOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  // V1.0.30: Modal breakdown KPI
  const [kpiBreakdownOpen, setKpiBreakdownOpen] = useState(false);

  // V1.0.51: Toggle hiển thị Người giao/Thực hiện/Theo dõi (list hoặc kanban)
  const PEOPLE_VIEW_KEY = 'hv-task-people-view';
  const [peopleView, setPeopleView] = useState<'list' | 'kanban'>(() => {
    const saved = localStorage.getItem(PEOPLE_VIEW_KEY);
    return saved === 'kanban' ? 'kanban' : 'list';
  });
  // Lưu preference mỗi khi đổi
  const handlePeopleViewChange = (v: 'list' | 'kanban') => {
    setPeopleView(v);
    localStorage.setItem(PEOPLE_VIEW_KEY, v);
  };
  const [editForm] = Form.useForm();
  const [subtaskForm] = Form.useForm();

  const { data: task, isLoading, error, refetch } = useQuery({
    queryKey: ['tasks', id],
    queryFn: () => taskService.getById(id!),
    enabled: !!id,
  });

  // Comments & History
  const { data: comments = [] } = useQuery({
    queryKey: ['task-comments', id],
    queryFn: () => taskService.getComments(id!),
    enabled: !!id,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['task-history', id],
    queryFn: () => taskService.getHistory(id!),
    enabled: !!id,
  });

  // V1.0.64: Tách history thành các nhóm sự kiện riêng để hiển thị inline
  // - progressUpdates: tất cả lần cập nhật progress + note (ascending theo thời gian)
  // - closureEvent: lần đóng task gần nhất (status = COMPLETED) + reason
  // - reopenEvent: lần trả lại task gần nhất (status từ COMPLETED/PENDING_REVIEW về IN_PROGRESS) + reason
  // - requestCloseEvent: lần đề nghị đóng task gần nhất + kết quả note
  const progressUpdates = useMemo(() => {
    return (history as any[])
      .filter(h => h.action === 'UPDATE_TASK_PROGRESS' && h.entityDescription)
      .map(h => {
        // Format BE: "progress=50% — <note>" hoặc chỉ "progress=50%"
        const desc = h.entityDescription as string;
        const m = /progress=(\d+)%(?:\s*—\s*(.+))?$/.exec(desc);
        return {
          id: h.id,
          createdAt: h.timestamp,
          actorName: h.username ?? h.user?.fullName ?? 'Hệ thống',
          progress: m ? parseInt(m[1], 10) : null,
          note: m?.[2]?.trim() ?? null,
        };
      })
      .reverse(); // BE trả desc → reverse để hiển thị cũ → mới
  }, [history]);

  const closureEvent = useMemo(() => {
    // V1.0.64-fix: Chỉ hiện banner "Task đã đóng" khi task HIỆN TẠI đang COMPLETED.
    // Nếu task từng đóng rồi bị trả lại → status quay về IN_PROGRESS → sẽ hiện banner "Trả lại" thay vì closure.
    if (!task || task.status !== 'COMPLETED') return null;

    const h = (history as any[]).find(h =>
      h.action === 'UPDATE_TASK_STATUS' &&
      typeof h.entityDescription === 'string' &&
      h.entityDescription.includes('→ COMPLETED')
    );
    if (!h) return null;
    const desc = h.entityDescription as string;
    const dashIdx = desc.indexOf(' — ');
    return {
      createdAt: h.timestamp,
      actorName: h.username ?? h.user?.fullName ?? 'Hệ thống',
      reason: dashIdx > -1 ? desc.substring(dashIdx + 3).trim() : null,
    };
  }, [history, task]);

  const reopenEvent = useMemo(() => {
    // V1.0.64-fix: "Trả lại task" = sau khi user đề nghị đóng (status PENDING_REVIEW)
    //              người duyệt KHÔNG đồng ý đóng → đưa task quay về IN_PROGRESS
    //
    // Phân biệt với "Bắt đầu thực hiện" lần đầu (NEW/ASSIGNED → IN_PROGRESS):
    //   - "Trả lại" CHỈ xảy ra khi history TRƯỚC ĐÓ có 1 event chuyển sang PENDING_REVIEW
    //   - Tức là phải có ít nhất 1 lần REQUEST_CLOSE_TASK trong history
    //
    // History từ BE sort desc (mới → cũ). Tìm event "→ IN_PROGRESS" gần nhất + verify
    // có REQUEST_CLOSE_TASK xảy ra TRƯỚC đó (= ở vị trí sau trong array desc).
    if (!task || task.status === 'COMPLETED' || task.status === 'CANCELLED') return null;

    const list = history as any[];
    let reopenIdx = -1;
    for (let i = 0; i < list.length; i++) {
      const h = list[i];
      if (h.action !== 'UPDATE_TASK_STATUS') continue;
      const desc = (h.entityDescription as string) ?? '';
      if (!desc.includes('→ IN_PROGRESS') && !desc.includes('→ ASSIGNED')) continue;
      // Phải có reason (= người duyệt nhập lý do trả lại)
      const dashIdx = desc.indexOf(' — ');
      if (dashIdx < 0) continue;
      reopenIdx = i;
      break;
    }
    if (reopenIdx < 0) return null;

    // Verify: trong history TRƯỚC reopen (array index > reopenIdx vì sort desc),
    //         phải có 1 event REQUEST_CLOSE_TASK chứng tỏ task từng được đề nghị đóng
    let hasRequestClose = false;
    for (let j = reopenIdx + 1; j < list.length; j++) {
      if (list[j].action === 'REQUEST_CLOSE_TASK') {
        hasRequestClose = true;
        break;
      }
      // Hoặc check status trước đó là PENDING_REVIEW
      const d = (list[j].entityDescription as string) ?? '';
      if (list[j].action === 'UPDATE_TASK_STATUS' && d.includes('→ PENDING_REVIEW')) {
        hasRequestClose = true;
        break;
      }
    }
    if (!hasRequestClose) return null;

    const h = list[reopenIdx];
    const desc = h.entityDescription as string;
    const dashIdx = desc.indexOf(' — ');
    const reason = desc.substring(dashIdx + 3).trim();
    return {
      createdAt: h.timestamp,
      actorName: h.username ?? h.user?.fullName ?? 'Hệ thống',
      reason,
    };
  }, [history, task]);

  const requestCloseEvent = useMemo(() => {
    // Đề nghị đóng task gần nhất (chưa được duyệt)
    if (!task || task.status !== 'PENDING_REVIEW') return null;
    const h = (history as any[]).find(h => h.action === 'REQUEST_CLOSE_TASK');
    if (!h) return null;
    const desc = (h.entityDescription as string) ?? '';
    // Format BE có thể là "Đề nghị đóng task ... Kết quả: <note>"
    const m = /Kết quả:\s*(.+)$/i.exec(desc);
    return {
      createdAt: h.timestamp,
      actorName: h.username ?? h.user?.fullName ?? 'Hệ thống',
      resultNote: m?.[1]?.trim() ?? null,
    };
  }, [history, task]);

  const { data: usersResp } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => userService.list({ pageSize: 500 }),
    enabled: subtaskOpen || editOpen, // V1.0.45: load cả khi mở edit modal
  });
  // V1.0.63i: Danh sách user cho tính năng tag @ trong comment (luôn load)
  const { data: mentionUsersResp } = useQuery({
    queryKey: ['users', 'for-mention'],
    queryFn: () => userService.list({ pageSize: 1000 }),
    staleTime: 5 * 60 * 1000,
  });
  const mentionUserOpts = (mentionUsersResp?.items ?? []).map((u: any) => ({
    value: u.id,
    label: `${u.fullName}${u.position ? ` — ${u.position}` : ''}`,
  }));

  // V1.0.45: Load org tree để chọn khoa khi edit
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => import('@/services/rolesService').then(m => m.orgService.getTree()),
    enabled: editOpen,
  });

  // V1.0.49: Load tags từ danh mục labels (chỉ khi mở edit modal)
  const { data: labelsList = [] } = useQuery({
    queryKey: ['labels-list'],
    queryFn: () => import('@/services/categoryService').then(m => m.labelService.list().catch(() => [])),
    enabled: editOpen,
  });
  const labelOptions = (labelsList as any[])
    .filter((l: any) => l.isActive !== false)
    .map((l: any) => ({ value: l.code ?? l.name, label: l.name }));

  // V1.0.45: Bỏ watchMutation (không còn nút Theo dõi tự nguyện trong UI)

  const commentMutation = useMutation({
    mutationFn: (value: RichEditorValue) => {
      // Serialize {html, attachments} → JSON string lưu vào content
      const serialized = serializeDescription(value);
      const attachmentIds = value.attachments.map(a => a.id);
      // V1.0.63i: gửi kèm mentionIds để notify người được tag
      return taskService.addComment(id!, serialized, mentionIds, attachmentIds);
    },
    onSuccess: () => {
      setNewComment({ html: '', attachments: [] });
      setMentionIds([]);  // V1.0.63i: reset tag
      qc.invalidateQueries({ queryKey: ['task-comments', id] });
      qc.invalidateQueries({ queryKey: ['task-history', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Không gửi được bình luận'),
  });

  const copyMutation = useMutation({
    mutationFn: () => taskService.copy(id!),
    onSuccess: (newTask: any) => {
      message.success(`Đã sao chép task: ${newTask.code}`);
      navigate(`/tasks/${newTask.id}`);
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Sao chép thất bại'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => taskService.cancel(id!),
    onSuccess: () => {
      message.success('Đã hủy task');
      refetch();
      qc.invalidateQueries({ queryKey: ['task-history', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Hủy thất bại'),
  });

  // V1.0.45: Xóa task hoàn toàn
  const deleteMutation = useMutation({
    mutationFn: () => taskService.delete(id!),
    onSuccess: () => {
      message.success('Đã xóa task');
      qc.invalidateQueries({ queryKey: ['tasks'] });
      navigate('/tasks');
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Xóa thất bại'),
  });

  // V1.0.25: Đánh dấu đang thực hiện
  const startMutation = useMutation({
    mutationFn: () => taskService.updateStatus(id!, 'IN_PROGRESS' as any, 'Bắt đầu thực hiện'),
    onSuccess: () => {
      message.success('Đã đánh dấu đang thực hiện');
      refetch();
      qc.invalidateQueries({ queryKey: ['task-history', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Không thực hiện được'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: any) => taskService.update(id!, payload),
    onSuccess: () => {
      message.success('Đã cập nhật task');
      setEditOpen(false);
      refetch();
      qc.invalidateQueries({ queryKey: ['task-history', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Cập nhật thất bại'),
  });

  const subtaskMutation = useMutation({
    mutationFn: (payload: any) => taskService.create(payload),
    onSuccess: () => {
      message.success('Đã thêm task con');
      setSubtaskOpen(false);
      subtaskForm.resetFields();
      refetch();
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Thêm task con thất bại'),
  });

  // Helpers
  const isCreator = task && currentUser && (task as any).createdBy === currentUser.id;
  const isCompleted = task?.status === 'COMPLETED';
  const isCancelled = task?.status === 'CANCELLED';
  const canEdit = isCreator && !isCompleted && !isCancelled;
  // V1.0.51: Watcher chỉ-xem (không phải creator/assigner/assignee) → ẩn các action sửa đổi
  const isAssigneeForMenu = task && currentUser &&
    task.assignees?.some((a: any) => a.user?.id === currentUser.id);
  const isAssignerForMenu = task && currentUser && task.assigner?.id === currentUser.id;
  const isWatcherOnlyForMenu = task && currentUser && !isCreator && !isAssigneeForMenu && !isAssignerForMenu &&
    (task.watchers ?? []).some((w: any) => w.id === currentUser.id);

  if (isLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;
  if (error || !task) {
    return (
      <Result
        status="404"
        title="Không tìm thấy task"
        extra={<Button onClick={() => navigate('/tasks')}>Quay lại danh sách</Button>}
      />
    );
  }

  const menuItems = [
    {
      key: 'edit',
      icon: <Edit2 size={14} />,
      label: 'Chỉnh sửa thông tin',
      disabled: !canEdit,
      onClick: () => {
        editForm.setFieldsValue({
          title: task.title,
          description: task.description,
          priority: task.priority,
          priorityLevel: (task as any).priorityLevel ?? 3,           // V1.0.26
          importantLevel: (task as any).importantLevel ?? 3,
          category: task.category,
          departmentId: task.department?.id,
          dueDate: dayjs(task.dueDate),
          startDate: task.startDate ? dayjs(task.startDate) : undefined,
          tags: task.tags ?? [],
          estimatedHours: (task as any).estimatedHours,
          kpiRevenuePlan: (task as any).kpiRevenuePlan ?? undefined,  // V1.0.30
          // V1.0.45: Load 4 mảng người để cho phép sửa
          // V1.0.54: assigneeIds đổi sang single → lấy MAIN đầu tiên (nếu task cũ có nhiều người, dữ liệu cũ bị mất nhưng vẫn lưu được)
          assigneeIds: (() => {
            const main = (task.assignees ?? []).find((a: any) => a.role === 'MAIN');
            return main?.user.id;
          })(),
          watcherIds: (task.watchers ?? []).map((w: any) => w.id),
          collaboratorIds: ((task as any).collaborators ?? []).map((u: any) => u.id),
          informedIds: ((task as any).informed ?? []).map((u: any) => u.id),
        });
        setEditOpen(true);
      },
    },
    {
      key: 'copy',
      icon: <CopyIcon size={14} />,
      label: 'Sao chép task',
      onClick: () => {
        modal.confirm({
          title: 'Sao chép task này?',
          content: 'Task mới sẽ giống task hiện tại (bao gồm người thực hiện, tags) với title "(Copy)".',
          okText: 'Sao chép',
          onOk: () => copyMutation.mutate(),
        });
      },
    },
    {
      key: 'cancel',
      icon: <XCircle size={14} />,
      label: 'Hủy task',
      danger: true,
      disabled: !isCreator || isCompleted || isCancelled,
      onClick: () => {
        modal.confirm({
          title: 'Hủy task này?',
          content: 'Task sẽ chuyển sang trạng thái CANCELLED. Thao tác này không thể hoàn tác.',
          okText: 'Hủy task',
          okButtonProps: { danger: true },
          cancelText: 'Quay lại',
          onOk: () => cancelMutation.mutate(),
        });
      },
    },
    {
      // V1.0.45: Xóa task — chỉ creator + chỉ khi NEW/ASSIGNED + progress=0
      key: 'delete',
      icon: <Trash2 size={14} />,
      label: 'Xóa task',
      danger: true,
      disabled: !isCreator
        || (task?.status !== 'NEW' && task?.status !== 'ASSIGNED')
        || ((task?.progress ?? 0) > 0),
      onClick: () => {
        modal.confirm({
          title: 'Xóa task này?',
          content: (
            <div>
              <div>Task <b>{task?.code}</b> sẽ bị xóa hoàn toàn khỏi hệ thống.</div>
              <div style={{ marginTop: 8, color: '#f5222d' }}>
                ⚠ Thao tác này không thể hoàn tác. Tất cả file đính kèm, người được giao, người phối hợp đều sẽ bị xóa cùng.
              </div>
              <div style={{ marginTop: 8, color: '#7d8a98', fontSize: 12 }}>
                Chỉ xóa được khi task chưa thực hiện gì (status = NEW/ASSIGNED, tiến độ = 0%).
              </div>
            </div>
          ),
          okText: 'Xóa hẳn',
          okButtonProps: { danger: true },
          cancelText: 'Quay lại',
          onOk: () => deleteMutation.mutate(),
        });
      },
    },
  ];

  return (
    <div style={{ background: '#fff', border: '1px solid #d4d9df', borderRadius: 8, overflow: 'hidden' }}>
      <Row>
        <Col xs={24} lg={16} style={{ padding: 24 }}>
          {(() => {
            // Smart back button: nếu task thuộc project → back về project, else về tasks list
            const projectId = (task as any).projectId ?? (task as any).project_id;
            if (projectId) {
              return (
                <Button
                  type="link"
                  icon={<ArrowLeft size={14} />}
                  onClick={() => navigate('/projects', { state: { selectProjectId: projectId } })}
                  style={{ padding: 0, fontSize: 12, color: '#7d8a98', marginBottom: 8 }}
                >
                  ← Quay lại dự án / {task.code}
                </Button>
              );
            }
            return (
              <Button
                type="link"
                icon={<ArrowLeft size={14} />}
                onClick={() => navigate('/tasks')}
                style={{ padding: 0, fontSize: 12, color: '#7d8a98', marginBottom: 8 }}
              >
                Task của tôi / {task.code}
              </Button>
            );
          })()}

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <TaskStatusBadge status={task.status} />
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, lineHeight: 1.3, color: '#1a1e23' }}>
                {task.title}
              </h1>
              <div className="mono" style={{ fontSize: 12, color: '#5b6875', marginTop: 6, fontWeight: 500 }}>
                <span style={{ color: '#1976D2' }}>{task.code}</span> · Tạo {dayjs(task.createdAt).format('DD/MM/YYYY HH:mm')} · Cập nhật{' '}
                {dayjs(task.updatedAt).format('DD/MM HH:mm')}
              </div>
              {/* V1.0.63i: Hiển thị task cha nếu đây là task con
                  - Nếu user có quyền xem task cha → clickable navigate
                  - Nếu không có quyền → chỉ hiện thông tin, không click được */}
              {task.parentTaskId && task.parentTaskTitle && (() => {
                const canAccess = task.parentTaskAccessible !== false;
                return (
                  <Tooltip
                    title={canAccess ? 'Click để mở task cha' : 'Bạn không có quyền xem chi tiết task cha này'}
                    placement="topLeft"
                  >
                    <div
                      onClick={canAccess ? () => navigate(`/tasks/${task.parentTaskId}`) : undefined}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        marginTop: 8, padding: '6px 12px',
                        background: canAccess ? '#f0f4f8' : '#f5f5f5',
                        border: '1px solid #d4dce5',
                        borderRadius: 6,
                        cursor: canAccess ? 'pointer' : 'not-allowed',
                        opacity: canAccess ? 1 : 0.75,
                        fontSize: 12,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { if (canAccess) e.currentTarget.style.background = '#e1e9f2'; }}
                      onMouseLeave={(e) => { if (canAccess) e.currentTarget.style.background = '#f0f4f8'; }}
                    >
                      <CornerUpLeft size={13} style={{ color: canAccess ? '#1976D2' : '#8a95a5', flexShrink: 0 }} />
                      <span style={{ color: '#7d8a98' }}>Task cha:</span>
                      {task.parentTaskCode && (
                        <span className="mono" style={{ color: canAccess ? '#1976D2' : '#5b6875', fontWeight: 600 }}>
                          {task.parentTaskCode}
                        </span>
                      )}
                      <span style={{ color: '#1a1e23', fontWeight: 500, maxWidth: 360,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {task.parentTaskTitle}
                      </span>
                      {task.parentTaskStatus && (
                        <TaskStatusBadge status={task.parentTaskStatus as any} />
                      )}
                      {!canAccess && (
                        <Lock size={11} style={{ color: '#8a95a5', marginLeft: 4 }} />
                      )}
                    </div>
                  </Tooltip>
                );
              })()}
            </div>
            {/* V1.0.45: Bỏ button Theo dõi tự nguyện. Watchers chỉ do creator gán khi tạo/sửa task. */}
            {/* V1.0.51: Watcher chỉ-xem không có quyền sửa task → ẩn menu */}
            {!isWatcherOnlyForMenu && (
              <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                <Button icon={<MoreHorizontal size={14} />} />
              </Dropdown>
            )}
          </div>

          {/* Progress */}
          <Card padding={14}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
              <b style={{ color: '#1a1e23' }}>
                Tiến độ
                {/* V1.0.53: Hint rõ khi auto-calc từ subtask */}
                {task.subtasks && task.subtasks.length > 0 && (
                  <Tooltip title="Tiến độ = trung bình cộng progress của các task con (bỏ qua task con đã hủy/lưu trữ).">
                    <span style={{
                      marginLeft: 6, fontSize: 10, color: '#1976D2',
                      background: '#e6f4ff', padding: '1px 6px', borderRadius: 4,
                      fontWeight: 600, cursor: 'help',
                    }}>
                      🤖 Tự động
                    </span>
                  </Tooltip>
                )}
              </b>
              <span className="mono" style={{ color: '#1976D2', fontWeight: 700, fontSize: 14 }}>
                {task.progress}%
                {task.subtasks && task.subtasks.length > 0 && (
                  <span style={{ color: '#5b6875', fontWeight: 500, fontSize: 12 }}>
                    {' · '}{task.subtasks.filter((s: any) => s.status === 'COMPLETED').length}/{task.subtasks.length} task con hoàn thành
                  </span>
                )}
              </span>
            </div>
            <Progress percent={task.progress} showInfo={false} strokeColor="#6699CC" />

            {/* V1.0.64: Lịch sử cập nhật tiến độ */}
            {progressUpdates.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 10px' }} />
                <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>
                  📝 LỊCH SỬ CẬP NHẬT ({progressUpdates.length})
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {progressUpdates.map((u, idx) => {
                    const prev = idx > 0 ? progressUpdates[idx - 1].progress : null;
                    const delta = prev != null && u.progress != null ? u.progress - prev : null;
                    return (
                      <div
                        key={u.id ?? idx}
                        style={{
                          padding: '8px 0',
                          borderBottom: idx < progressUpdates.length - 1 ? '1px dashed #ececec' : 'none',
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{
                          flexShrink: 0,
                          minWidth: 48,
                          textAlign: 'center',
                          background: '#e6f4ff',
                          border: '1px solid #91caff',
                          borderRadius: 4,
                          padding: '2px 6px',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#1976D2',
                        }}>
                          {u.progress != null ? `${u.progress}%` : '—'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 2 }}>
                            <b style={{ color: '#5b6875' }}>{u.actorName}</b>
                            {' · '}
                            {dayjs(u.createdAt).format('DD/MM/YYYY HH:mm')}
                            {delta != null && delta !== 0 && (
                              <span style={{
                                marginLeft: 6,
                                fontSize: 10,
                                color: delta > 0 ? '#52c41a' : '#fa541c',
                                fontWeight: 600,
                              }}>
                                {delta > 0 ? `+${delta}` : delta}%
                              </span>
                            )}
                          </div>
                          {u.note ? (
                            <div style={{
                              fontSize: 12,
                              color: '#1a1e23',
                              background: '#fafafa',
                              borderLeft: '3px solid #6699CC',
                              padding: '5px 8px',
                              borderRadius: 3,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: 1.45,
                            }}>
                              {u.note}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: '#a5b0c0', fontStyle: 'italic' }}>
                              (không có ghi chú)
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* V1.0.30: KPI Doanh thu panel — hiển thị khi root có plan */}
          {(() => {
            // Logic hiển thị:
            // - Task là ROOT (không có parent) và có kpiRevenuePlan > 0 → hiện full panel
            // - Task có rootKpiPlan > 0 (là descendant của 1 cây có KPI) → hiện panel cảnh báo
            const isRoot = !task.parentTaskId;
            const rootPlan = task.rootKpiPlan ?? task.kpiRevenuePlan ?? 0;
            const hasKpi = rootPlan > 0;
            if (!hasKpi) return null;

            const aggregatedActual = task.aggregatedKpiActual ?? 0;
            const myActual = task.kpiRevenueActual ?? 0;
            const progressPct = rootPlan > 0
              ? Math.min(100, Math.round((aggregatedActual / rootPlan) * 1000) / 10)
              : 0;
            // Màu thanh progress KPI theo %
            const kpiStrokeColor = progressPct >= 100 ? '#52c41a'
              : progressPct >= 70 ? '#1976D2'
              : progressPct >= 40 ? '#fa8c16'
              : '#cf1322';

            return (
              <div style={{ marginTop: 12 }}>
                <div style={{
                  background: '#fffbf0',
                  border: '1px solid #ffe7ba',
                  borderRadius: 8,
                  padding: 14,
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <b style={{ color: '#7c3a00', fontSize: 13 }}>💰 KPI Doanh thu</b>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setKpiBreakdownOpen(true)}
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  >
                    Xem chi tiết KPI →
                  </Button>
                </div>

                {isRoot ? (
                  <>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: 12, color: '#5b6875', marginBottom: 6,
                    }}>
                      <span>
                        Đã đạt: <b style={{ color: '#1a1e23' }}>{aggregatedActual.toLocaleString('vi-VN')} đ</b>
                      </span>
                      <span>
                        Kế hoạch: <b style={{ color: '#1a1e23' }}>{rootPlan.toLocaleString('vi-VN')} đ</b>
                      </span>
                    </div>
                    <Progress
                      percent={progressPct}
                      strokeColor={kpiStrokeColor}
                      format={(p) => `${p?.toFixed(1)}%`}
                    />
                    <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4, fontStyle: 'italic' }}>
                      Tổng từ task này và toàn bộ task con/cháu đã đề nghị đóng.
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: '#5b6875' }}>
                    Task này nằm trong cây có <b style={{ color: '#7c3a00' }}>KPI gốc {rootPlan.toLocaleString('vi-VN')} đ</b>.
                    Khi đề nghị đóng, bạn cần nhập doanh thu thực tế đã đạt.
                    {myActual > 0 && (
                      <div style={{ marginTop: 4 }}>
                        KPI thực tế task này đã ghi nhận: <b style={{ color: '#16a34a' }}>{myActual.toLocaleString('vi-VN')} đ</b>
                      </div>
                    )}
                  </div>
                )}
                </div>
              </div>
            );
          })()}

          {/* Description */}
          <Divider style={{ margin: '20px 0 12px' }} />
          <div style={{ fontSize: 11, fontWeight: 600, color: '#48535e', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Mô tả
          </div>
          <Card padding={14}>
            {task.description ? (() => {
              const parsed = parseDescription(task.description);
              return <RichContent html={sanitizeHtml(parsed.html)} attachments={parsed.attachments} />;
            })() : (
              <div style={{ fontSize: 13, color: '#a5b0c0', fontStyle: 'italic' }}>(không có mô tả)</div>
            )}
          </Card>

          {/* Subtasks */}
          <Divider style={{ margin: '24px 0 12px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#48535e', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Task con ({task.subtasks?.length ?? 0})
            </div>
            {/* V1.0.51: Watcher chỉ-xem không được thêm task con */}
            {!isWatcherOnlyForMenu && (
              <Button type="link" size="small" icon={<Plus size={12} />} style={{ color: '#6699CC' }}
                onClick={() => {
                  subtaskForm.setFieldsValue({
                    priority: 'MEDIUM',                // hidden field - cho BE compat
                    priorityLevel: task.priorityLevel ?? 3,    // V1.0.26: kế thừa từ task cha
                    importantLevel: (task as any).importantLevel ?? 3,
                    category: task.category,
                    dueDate: dayjs(task.dueDate),
                    // V1.0.59: Pre-fill watcher/collab/informed từ task cha (user vẫn sửa được)
                    watcherIds: (task.watchers ?? []).map((w: any) => w.id),
                    collaboratorIds: ((task as any).collaborators ?? []).map((u: any) => u.id),
                    informedIds: ((task as any).informed ?? []).map((u: any) => u.id),
                  });
                  setSubtaskOpen(true);
                }}>
                Thêm task con
              </Button>
            )}
          </div>
          <Card padding={0}>
            {!task.subtasks || task.subtasks.length === 0 ? (
              <Empty description="Chưa có task con" style={{ padding: 24 }} />
            ) : task.subtasks.map((st: any, i: number) => (
              <div
                key={st.id}
                onClick={() => navigate(`/tasks/${st.id}`)}
                style={{
                  padding: 12,
                  borderBottom: i === task.subtasks!.length - 1 ? 0 : '1px solid #eceef1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <Checkbox checked={st.status === 'COMPLETED'} onClick={(e) => e.stopPropagation()} />
                <span style={{
                  flex: 1,
                  fontSize: 13,
                  textDecoration: st.status === 'COMPLETED' ? 'line-through' : 'none',
                  color: st.status === 'COMPLETED' ? '#adb6c0' : '#3a434c',
                }}>
                  {st.title}
                </span>
                <TaskStatusBadge status={st.status} />
                <span style={{ fontSize: 11, color: '#adb6c0' }}>
                  {dayjs(st.dueDate).format('DD/MM')}
                </span>
              </div>
            ))}
          </Card>

          {/* Attachments */}
          <Divider style={{ margin: '24px 0 12px' }} />
          <div style={{ fontSize: 11, fontWeight: 600, color: '#48535e', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Tài liệu đính kèm
          </div>
          <TaskAttachmentsPanel taskId={task.id} />

          {/* Comments + History */}
          <Divider style={{ margin: '24px 0 12px' }} />
          <Tabs
            items={[
              {
                key: 'comments',
                label: `Bình luận (${comments.length})`,
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {comments.length === 0 && (
                      <Empty description="Chưa có bình luận" style={{ padding: 24 }} />
                    )}
                    {comments.map((c: any) => {
                      const isMe = c.author?.id === currentUser?.id;
                      const parsed = parseDescription(c.content);
                      return (
                        <div key={c.id} style={{ display: 'flex', gap: 12 }}>
                          <Avatar
                            src={c.author?.id ? `/api/auth/avatar/${c.author.id}` : undefined}
                            style={{ background: isMe ? '#2a3547' : '#d4d9df', color: '#6699CC', flexShrink: 0 }}
                          >
                            {c.author?.fullName?.slice(0, 2)?.toUpperCase() ?? 'U'}
                          </Avatar>
                          <Card padding={12}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
                              <span style={{ fontWeight: 600, fontSize: 13 }}>
                                {isMe ? `Bạn (${c.author?.fullName})` : c.author?.fullName}
                              </span>
                              <span className="mono" style={{ fontSize: 11, color: '#adb6c0' }}>
                                {dayjs(c.createdAt).format('DD/MM HH:mm')}
                              </span>
                            </div>
                            <RichContent
                              html={sanitizeHtml(parsed.html)}
                              attachments={parsed.attachments.length > 0 ? parsed.attachments : c.attachments}
                            />
                          </Card>
                        </div>
                      );
                    })}

                    {/* Input */}
                    <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
                      <Avatar
                        src={currentUser?.id ? `/api/auth/avatar/${currentUser.id}` : undefined}
                        style={{ background: '#2a3547', color: '#6699CC', flexShrink: 0 }}
                      >
                        {currentUser?.fullName?.slice(0, 2)?.toUpperCase() ?? 'U'}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <RichEditor
                          value={newComment}
                          onChange={setNewComment}
                          placeholder="Viết bình luận... (có thể chèn ảnh, link, emoji, đính kèm file)"
                          minHeight={70}
                          maxHeight={300}
                          simplified={true}
                          showAttachments={true}
                        />
                        {/* V1.0.63i: Tag @ người dùng để notify */}
                        <div style={{ marginTop: 8 }}>
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="@ Nhắc đến người dùng (họ sẽ nhận thông báo)..."
                            value={mentionIds}
                            onChange={setMentionIds}
                            options={mentionUserOpts}
                            maxTagCount="responsive"
                          />
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="primary"
                            size="small"
                            loading={commentMutation.isPending}
                            disabled={isEmptyHtml(newComment.html) && newComment.attachments.length === 0}
                            onClick={() => commentMutation.mutate(newComment)}
                          >
                            Gửi
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'history',
                label: `Lịch sử (${history.length})`,
                children: history.length === 0 ? (
                  <Empty description="Chưa có hoạt động" style={{ padding: 24 }} />
                ) : (
                  <Timeline
                    items={history.map((h: any) => {
                      // V1.0.57: Tách "note/reason" ra khỏi prefix kỹ thuật để hiển thị nổi bật
                      // Format BE: "progress=50% — <note>" hoặc "→ COMPLETED — <reason>" hoặc "...prefix - Kết quả: <note>"
                      let prefix: string | null = null;
                      let userNote: string | null = null;
                      const desc = h.entityDescription as string | undefined;
                      if (desc) {
                        // Pattern 1: "<prefix> — <userNote>" (em-dash separator)
                        const dashIdx = desc.indexOf(' — ');
                        if (dashIdx > -1) {
                          prefix = desc.substring(0, dashIdx);
                          userNote = desc.substring(dashIdx + 3).trim();
                        } else {
                          prefix = desc;
                        }
                      }
                      return ({
                        color: h.result === 'SUCCESS' ? 'blue' : 'red',
                        dot: <History size={12} />,
                        children: (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>
                              {actionLabel(h.action)}
                              {prefix && <span style={{ color: '#7d8a98', fontWeight: 400 }}> — {prefix}</span>}
                            </div>
                            {userNote && (
                              <div style={{
                                fontSize: 12, color: '#1a1e23',
                                background: '#f6f7f8',
                                borderLeft: '3px solid #6699CC',
                                padding: '6px 10px',
                                borderRadius: 4,
                                marginTop: 4,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}>
                                💬 {userNote}
                              </div>
                            )}
                            <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 2 }}>
                              {h.username ?? 'Hệ thống'} · {dayjs(h.timestamp).format('DD/MM/YYYY HH:mm')}
                              {h.ip && <> · IP {h.ip}</>}
                            </div>
                            {h.failureReason && (
                              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                                Lỗi: {h.failureReason}
                              </div>
                            )}
                          </div>
                        ),
                      });
                    })}
                  />
                ),
              },
            ]}
          />
        </Col>

        {/* Right meta panel */}
        <Col xs={24} lg={8} style={{ padding: 20, background: '#f6f7f8', borderLeft: '1px solid #eceef1' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* V1.0.51: Toggle hiển thị người giao/thực hiện/theo dõi */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: -8 }}>
              <span style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Nhân sự
              </span>
              <Segmented
                size="small"
                value={peopleView}
                onChange={(v) => handlePeopleViewChange(v as 'list' | 'kanban')}
                options={[
                  { value: 'list', label: 'Danh sách' },
                  { value: 'kanban', label: 'Kanban' },
                ]}
              />
            </div>

            {peopleView === 'list' ? (
              // ============ LIST VIEW (mặc định, giữ nguyên giao diện cũ) ============
              <>
                <div>
                  <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Người giao</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <Avatar size="small" style={{ background: '#d4d9df' }}>{task.assigner.fullName.slice(-1)}</Avatar>
                    <span style={{ color: '#1a1e23', fontWeight: 500 }}>{task.assigner.fullName}</span>{' '}
                    {task.assigner.position && (
                      <span style={{ color: '#7d8a98', fontSize: 12 }}>({task.assigner.position})</span>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Người thực hiện ({task.assignees.length})
                  </div>
                  {task.assignees.map((a) => (
                    <div key={a.user.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
                      <Avatar size="small" style={{ background: '#2a3547', color: '#6699CC' }}>
                        {a.user.fullName.slice(-1)}
                      </Avatar>
                      <span style={{ color: '#1a1e23', fontWeight: 500 }}>{a.user.fullName}</span>
                      <Tag color={a.role === 'MAIN' ? 'cyan' : 'default'} style={{ fontSize: 11, fontWeight: 600 }}>
                        {a.role === 'MAIN' ? 'Chính' : 'Hỗ trợ'}
                      </Tag>
                    </div>
                  ))}
                </div>

                {task.watchers && task.watchers.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Người theo dõi ({task.watchers.length})
                    </div>
                    {task.watchers.map((w) => (
                      <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
                        <Avatar size="small" style={{ background: '#eceef1', color: '#5b6875' }}>
                          {w.fullName.slice(-1)}
                        </Avatar>
                        <span style={{ color: '#1a1e23', fontWeight: 500 }}>{w.fullName}</span>
                        <Tag style={{ fontSize: 11 }}>👁 Theo dõi</Tag>
                      </div>
                    ))}
                  </div>
                )}

                {/* V1.0.63i: Người phối hợp */}
                {task.collaborators && task.collaborators.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Người phối hợp ({task.collaborators.length})
                    </div>
                    {task.collaborators.map((c) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
                        <Avatar size="small" style={{ background: '#e6f4ff', color: '#1677ff' }}>
                          {c.fullName.slice(-1)}
                        </Avatar>
                        <span style={{ color: '#1a1e23', fontWeight: 500 }}>{c.fullName}</span>
                        <Tag color="blue" style={{ fontSize: 11 }}>🤝 Phối hợp</Tag>
                      </div>
                    ))}
                  </div>
                )}

                {/* V1.0.63i: Người cung cấp thông tin */}
                {task.informed && task.informed.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Người cung cấp thông tin ({task.informed.length})
                    </div>
                    {task.informed.map((i) => (
                      <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
                        <Avatar size="small" style={{ background: '#fff7e6', color: '#d46b08' }}>
                          {i.fullName.slice(-1)}
                        </Avatar>
                        <span style={{ color: '#1a1e23', fontWeight: 500 }}>{i.fullName}</span>
                        <Tag color="orange" style={{ fontSize: 11 }}>💡 Cung cấp TT</Tag>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              // ============ KANBAN VIEW (3 cột + Row 2 phối hợp/cung cấp TT) ============
              <>
              <Row gutter={8}>
                {/* Cột 1 - Người giao */}
                <Col span={8}>
                  <div style={{
                    background: '#fff', border: '1px solid #e1e5eb', borderRadius: 6,
                    padding: 8, minHeight: 120,
                  }}>
                    <div style={{
                      fontSize: 10, color: '#5b6875', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.3, marginBottom: 8, paddingBottom: 6,
                      borderBottom: '2px solid #d4d9df', textAlign: 'center',
                    }}>
                      Người giao (1)
                    </div>
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      gap: 4, padding: 6, background: '#f6f7f8', borderRadius: 4,
                    }}>
                      <Avatar size={32} style={{ background: '#d4d9df', color: '#5b6875' }}>
                        {task.assigner.fullName.slice(-1)}
                      </Avatar>
                      <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
                        {task.assigner.fullName}
                      </span>
                      {task.assigner.position && (
                        <span style={{ fontSize: 10, color: '#7d8a98', textAlign: 'center' }}>
                          {task.assigner.position}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Cột 2 - Người thực hiện */}
                <Col span={8}>
                  <div style={{
                    background: '#fff', border: '1px solid #adc6ff', borderRadius: 6,
                    padding: 8, minHeight: 120,
                  }}>
                    <div style={{
                      fontSize: 10, color: '#1d39c4', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.3, marginBottom: 8, paddingBottom: 6,
                      borderBottom: '2px solid #adc6ff', textAlign: 'center',
                    }}>
                      Thực hiện ({task.assignees.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {task.assignees.map((a) => (
                        <div key={a.user.id} style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          gap: 4, padding: 6, background: '#f0f5ff', borderRadius: 4,
                        }}>
                          <Avatar size={32} style={{ background: '#2a3547', color: '#6699CC' }}>
                            {a.user.fullName.slice(-1)}
                          </Avatar>
                          <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
                            {a.user.fullName}
                          </span>
                          <Tag color={a.role === 'MAIN' ? 'cyan' : 'default'} style={{ fontSize: 10, fontWeight: 600, margin: 0 }}>
                            {a.role === 'MAIN' ? 'Chính' : 'Hỗ trợ'}
                          </Tag>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>

                {/* Cột 3 - Người theo dõi */}
                <Col span={8}>
                  <div style={{
                    background: '#fff', border: '1px solid #ffd591', borderRadius: 6,
                    padding: 8, minHeight: 120,
                  }}>
                    <div style={{
                      fontSize: 10, color: '#d46b08', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.3, marginBottom: 8, paddingBottom: 6,
                      borderBottom: '2px solid #ffd591', textAlign: 'center',
                    }}>
                      Theo dõi ({task.watchers?.length ?? 0})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(task.watchers && task.watchers.length > 0) ? (
                        task.watchers.map((w) => (
                          <div key={w.id} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            gap: 4, padding: 6, background: '#fff7e6', borderRadius: 4,
                          }}>
                            <Avatar size={32} style={{ background: '#eceef1', color: '#5b6875' }}>
                              {w.fullName.slice(-1)}
                            </Avatar>
                            <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
                              {w.fullName}
                            </span>
                            <Tag style={{ fontSize: 10, margin: 0 }}>👁 Theo dõi</Tag>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: 11, color: '#adb6c0', textAlign: 'center', padding: '12px 0' }}>
                          Chưa có
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* V1.0.63i: Row 2 - Người phối hợp + Người cung cấp thông tin */}
              {((task.collaborators && task.collaborators.length > 0)
                || (task.informed && task.informed.length > 0)) && (
                <Row gutter={8} style={{ marginTop: 8 }}>
                  {/* Cột 1 - Người phối hợp */}
                  <Col xs={24} md={12}>
                    <div style={{
                      background: '#fff', border: '1px solid #e1e5eb', borderRadius: 6,
                      padding: 8, minHeight: 120,
                    }}>
                      <div style={{
                        fontSize: 10, color: '#1677ff', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.3, marginBottom: 8, paddingBottom: 6,
                        borderBottom: '2px solid #91caff', textAlign: 'center',
                      }}>
                        Người phối hợp ({task.collaborators?.length ?? 0})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                        {(task.collaborators && task.collaborators.length > 0) ? (
                          task.collaborators.map((c) => (
                            <div key={c.id} style={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              gap: 4, padding: 6, background: '#e6f4ff', borderRadius: 4, minWidth: 80,
                            }}>
                              <Avatar size={32} style={{ background: '#bae0ff', color: '#0958d9' }}>
                                {c.fullName.slice(-1)}
                              </Avatar>
                              <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
                                {c.fullName}
                              </span>
                              <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>🤝 Phối hợp</Tag>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: 11, color: '#adb6c0', textAlign: 'center', padding: '12px 0' }}>
                            Chưa có
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                  {/* Cột 2 - Người cung cấp thông tin */}
                  <Col xs={24} md={12}>
                    <div style={{
                      background: '#fff', border: '1px solid #e1e5eb', borderRadius: 6,
                      padding: 8, minHeight: 120,
                    }}>
                      <div style={{
                        fontSize: 10, color: '#d46b08', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.3, marginBottom: 8, paddingBottom: 6,
                        borderBottom: '2px solid #ffd591', textAlign: 'center',
                      }}>
                        Người cung cấp thông tin ({task.informed?.length ?? 0})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                        {(task.informed && task.informed.length > 0) ? (
                          task.informed.map((i) => (
                            <div key={i.id} style={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              gap: 4, padding: 6, background: '#fff7e6', borderRadius: 4, minWidth: 80,
                            }}>
                              <Avatar size={32} style={{ background: '#ffe7ba', color: '#ad4e00' }}>
                                {i.fullName.slice(-1)}
                              </Avatar>
                              <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>
                                {i.fullName}
                              </span>
                              <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>💡 Cung cấp TT</Tag>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: 11, color: '#adb6c0', textAlign: 'center', padding: '12px 0' }}>
                            Chưa có
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              </>
            )}

            <Divider style={{ margin: 0 }} />

            <Row gutter={[12, 12]} style={{ fontSize: 13 }}>
              {/* V1.0.26: 2 trục Eisenhower thay cho Ưu tiên + Mức độ + Cấp độ */}
              {/* V1.0.52: Dùng gradient màu vàng→đỏ (getLevelColor) + hiện ngôi sao mini */}
              <Col span={12}>
                <div style={{ color: '#5b6875', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>🔴 Gấp</div>
                {(() => {
                  const lvl = (task as any).priorityLevel ?? 3;
                  const color = getLevelColor(lvl);
                  const labels: Record<number, string> = {
                    5: 'Rất gấp', 4: 'Gấp', 3: 'Bình thường', 2: 'Ít gấp', 1: 'Không gấp',
                  };
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ color, fontSize: 14, letterSpacing: 1 }}>
                        {'★'.repeat(lvl)}<span style={{ color: '#e8e8e8' }}>{'★'.repeat(5 - lvl)}</span>
                      </span>
                      <Tag style={{
                        background: 'transparent',
                        border: `1.5px solid ${color}`,
                        color,
                        fontWeight: 700,
                        margin: 0,
                      }}>
                        Cấp {lvl} — {labels[lvl]}
                      </Tag>
                    </div>
                  );
                })()}
              </Col>
              <Col span={12}>
                <div style={{ color: '#5b6875', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>🟠 Quan trọng</div>
                {(() => {
                  const lvl = (task as any).importantLevel ?? 3;
                  const color = getLevelColor(lvl);
                  const labels: Record<number, string> = {
                    5: 'Rất q.trọng', 4: 'Quan trọng', 3: 'Bình thường', 2: 'Ít q.trọng', 1: 'Không q.trọng',
                  };
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ color, fontSize: 14, letterSpacing: 1 }}>
                        {'★'.repeat(lvl)}<span style={{ color: '#e8e8e8' }}>{'★'.repeat(5 - lvl)}</span>
                      </span>
                      <Tag style={{
                        background: 'transparent',
                        border: `1.5px solid ${color}`,
                        color,
                        fontWeight: 700,
                        margin: 0,
                      }}>
                        Cấp {lvl} — {labels[lvl]}
                      </Tag>
                    </div>
                  );
                })()}
              </Col>
              <Col span={12}>
                <div style={{ color: '#5b6875', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Loại</div>
                <Tag>{task.category}</Tag>
              </Col>
              <Col span={12}>
                <div style={{ color: '#5b6875', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Khoa/Phòng</div>
                <span style={{ color: '#1a1e23', fontWeight: 500 }}>{task.department.name}</span>
              </Col>
              <Col span={12}>
                <div style={{ color: '#5b6875', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Hạn</div>
                <span style={{ color: task.status === 'OVERDUE' ? '#dc2626' : '#1a1e23', fontWeight: 600 }}>
                  {dayjs(task.dueDate).format('DD/MM/YYYY')}
                </span>
              </Col>
            </Row>

            {/* V1.0.64: Banner hiển thị nội dung đóng task / trả lại task / đề nghị đóng */}
            {closureEvent && (
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: closureEvent.reason ? 6 : 0 }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  <b style={{ color: '#389e0d' }}>Task đã đóng</b>
                  <span style={{ fontSize: 11, color: '#7d8a98', marginLeft: 'auto' }}>
                    {closureEvent.actorName} · {dayjs(closureEvent.createdAt).format('DD/MM/YYYY HH:mm')}
                  </span>
                </div>
                {closureEvent.reason && (
                  <div style={{
                    fontSize: 12,
                    color: '#1a1e23',
                    background: '#fff',
                    borderLeft: '3px solid #52c41a',
                    padding: '6px 10px',
                    borderRadius: 3,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                  }}>
                    <span style={{ color: '#7d8a98', fontWeight: 600, marginRight: 6 }}>Lý do đóng:</span>
                    {closureEvent.reason}
                  </div>
                )}
              </div>
            )}

            {reopenEvent && (
              <div style={{
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>↩️</span>
                  <b style={{ color: '#d46b08' }}>Task đã được trả lại</b>
                  <span style={{ fontSize: 11, color: '#7d8a98', marginLeft: 'auto' }}>
                    {reopenEvent.actorName} · {dayjs(reopenEvent.createdAt).format('DD/MM/YYYY HH:mm')}
                  </span>
                </div>
                <div style={{
                  fontSize: 12,
                  color: '#1a1e23',
                  background: '#fff',
                  borderLeft: '3px solid #fa8c16',
                  padding: '6px 10px',
                  borderRadius: 3,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                }}>
                  <span style={{ color: '#7d8a98', fontWeight: 600, marginRight: 6 }}>Lý do trả lại:</span>
                  {reopenEvent.reason}
                </div>
              </div>
            )}

            {requestCloseEvent && !closureEvent && (
              <div style={{
                background: '#e6f4ff',
                border: '1px solid #91caff',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: requestCloseEvent.resultNote ? 6 : 0 }}>
                  <span style={{ fontSize: 16 }}>⏳</span>
                  <b style={{ color: '#1976D2' }}>Đề nghị đóng task — đang chờ duyệt</b>
                  <span style={{ fontSize: 11, color: '#7d8a98', marginLeft: 'auto' }}>
                    {requestCloseEvent.actorName} · {dayjs(requestCloseEvent.createdAt).format('DD/MM/YYYY HH:mm')}
                  </span>
                </div>
                {requestCloseEvent.resultNote && (
                  <div style={{
                    fontSize: 12,
                    color: '#1a1e23',
                    background: '#fff',
                    borderLeft: '3px solid #1976D2',
                    padding: '6px 10px',
                    borderRadius: 3,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                  }}>
                    <span style={{ color: '#7d8a98', fontWeight: 600, marginRight: 6 }}>Kết quả báo cáo:</span>
                    {requestCloseEvent.resultNote}
                  </div>
                )}
              </div>
            )}

            {task.tags && task.tags.length > 0 && (
              <>
                <Divider style={{ margin: 0 }} />
                <div>
                  <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 6 }}>Nhãn</div>
                  <Space wrap size={[4, 4]}>
                    {task.tags.map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </Space>
                </div>
              </>
            )}

            <Divider style={{ margin: 0 }} />

            {/* ================== ACTIONS THEO ROLE + STATUS ================== */}
            <Space direction="vertical" style={{ width: '100%' }}>
              {(() => {
                const isAssignee = task.assignees?.some((a: any) => a.user?.id === currentUser?.id);
                const isApprover = task.createdBy === currentUser?.id ||
                                   task.assigner?.id === currentUser?.id;
                const isCurrentUserCreator = task.createdBy === currentUser?.id;
                // V1.0.51: Watcher chỉ-xem (không phải assignee, không phải creator/assigner)
                const isWatcherOnly = !isAssignee && !isApprover &&
                                      (task.watchers ?? []).some((w: any) => w.id === currentUser?.id);
                const status = task.status;

                // --- V1.0.51: Watcher only → banner read-only, không hiện nút action ---
                if (isWatcherOnly) {
                  return (
                    <div style={{
                      background: '#fff7e6', border: '1px solid #ffd591',
                      padding: 10, borderRadius: 6, fontSize: 12, color: '#d46b08',
                    }}>
                      👁 Bạn đang <b>theo dõi</b> task này. Chỉ có quyền xem và bình luận — không thể tác động tiến độ, trạng thái hay nội dung task.
                    </div>
                  );
                }

                // --- Người giao việc khi task ĐANG CHỜ DUYỆT → Duyệt hoặc Trả lại ---
                if (isApprover && status === 'PENDING_REVIEW') {
                  return (
                    <>
                      <div style={{
                        background: '#fff7e6', border: '1px solid #ffd591',
                        padding: 10, borderRadius: 6, fontSize: 12, color: '#d46b08',
                        marginBottom: 4,
                      }}>
                        <b>Task đang chờ bạn duyệt.</b> Người thực hiện đã báo hoàn thành — kiểm tra kết quả và quyết định đóng hoặc trả lại.
                      </div>
                      <Button type="primary" block icon={<CheckCircle2 size={14} />}
                        onClick={() => setApproveOpen(true)}>
                        Duyệt & đóng task
                      </Button>
                      <Button block danger icon={<AlertTriangle size={14} />}
                        onClick={() => setSendBackOpen(true)}>
                        Trả lại để làm tiếp
                      </Button>
                    </>
                  );
                }

                // --- Người thực hiện khi task ĐANG LÀM → Cập nhật tiến độ + Đề nghị đóng ---
                if (isAssignee && (status === 'ASSIGNED' || status === 'IN_PROGRESS' || status === 'NEW')) {
                  // V1.0.53: Task có subtask ACTIVE → progress tính tự động, khóa nút "Cập nhật tiến độ"
                  // Subtask CANCELLED/ARCHIVED không tính → BE auto-mở lại khi xóa hết subtask active
                  const activeSubtasks = (task.subtasks ?? [])
                    .filter((s: any) => s.status !== 'CANCELLED' && s.status !== 'ARCHIVED');
                  const hasActiveSubtasks = activeSubtasks.length > 0;
                  return (
                    <>
                      {/* V1.0.25: Đánh dấu đang thực hiện - chỉ hiện khi NEW/ASSIGNED */}
                      {(status === 'NEW' || status === 'ASSIGNED') && (
                        <Button
                          type="primary"
                          block
                          icon={<Play size={14} />}
                          loading={startMutation.isPending}
                          onClick={() => startMutation.mutate()}
                          style={{ background: '#52c41a', borderColor: '#52c41a' }}
                        >
                          Đánh dấu đang thực hiện
                        </Button>
                      )}
                      <Button type={status === 'IN_PROGRESS' ? 'primary' : 'default'} block icon={<CheckCircle2 size={14} />}
                        onClick={() => setSubmitOpen(true)}>
                        Đề nghị đóng task
                      </Button>

                      {/* V1.0.53: Nếu có task con ACTIVE → khóa Cập nhật tiến độ + show info */}
                      {hasActiveSubtasks ? (
                        <div style={{
                          background: '#f0f5ff', border: '1px solid #adc6ff',
                          padding: 8, borderRadius: 6, fontSize: 11, color: '#1d39c4',
                        }}>
                          ℹ Tiến độ tính tự động: trung bình cộng progress của {activeSubtasks.length} task con.
                        </div>
                      ) : (
                        <Button block onClick={() => setProgressOpen(true)}>
                          Cập nhật tiến độ
                        </Button>
                      )}

                      {/* V1.0.45: Nếu user là CẢ creator + assignee → cho phép Đóng trực tiếp */}
                      {isCurrentUserCreator && (
                        <Button block type="primary" ghost
                          icon={<CheckCircle2 size={14} />}
                          onClick={() => setApproveOpen(true)}
                          style={{ borderColor: '#52c41a', color: '#52c41a' }}
                        >
                          Đóng task trực tiếp (vì tôi là người tạo)
                        </Button>
                      )}
                    </>
                  );
                }

                // V1.0.45: Người tạo task (KHÔNG phải assignee) → vẫn cho phép Đóng task khi đang chạy
                // Fix Y/c 2: Trước đây creator không complete được khi không phải assignee.
                if (isCurrentUserCreator && !isAssignee
                    && (status === 'ASSIGNED' || status === 'IN_PROGRESS' || status === 'NEW')) {
                  return (
                    <>
                      <div style={{
                        background: '#e6f7ff', border: '1px solid #91d5ff',
                        padding: 10, borderRadius: 6, fontSize: 12, color: '#0958d9',
                        marginBottom: 4,
                      }}>
                        <b>Bạn là người tạo task này.</b> Có thể đóng task bất cứ lúc nào không cần đợi người thực hiện đề nghị đóng.
                      </div>
                      <Button type="primary" block icon={<CheckCircle2 size={14} />}
                        onClick={() => setApproveOpen(true)}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Đóng task & đánh dấu hoàn thành
                      </Button>
                    </>
                  );
                }

                // --- Người thực hiện khi task ĐANG CHỜ DUYỆT → chờ ---
                if (isAssignee && status === 'PENDING_REVIEW') {
                  return (
                    <div style={{
                      background: '#f0f5ff', border: '1px solid #adc6ff',
                      padding: 10, borderRadius: 6, fontSize: 12, color: '#1d39c4',
                    }}>
                      Task đang chờ người giao việc duyệt. Nếu được trả lại, bạn có thể tiếp tục làm.
                    </div>
                  );
                }

                // --- Task đã đóng → KHÔNG hiển thị card nữa vì banner "Task đã đóng"
                //     (với lý do + người đóng + thời gian) đã hiện ở panel trên ---
                if (status === 'COMPLETED') {
                  return null;
                }

                // --- Task bị hủy → vẫn cần card riêng (khác hoàn thành) ---
                if (status === 'CANCELLED') {
                  return (
                    <div style={{
                      background: '#fafafa',
                      border: '1px solid #d9d9d9',
                      padding: 10, borderRadius: 6, fontSize: 12,
                      color: '#595959',
                    }}>
                      Task đã bị hủy.
                    </div>
                  );
                }

                // --- Người xem không phải assignee cũng không phải approver → chỉ xem ---
                return (
                  <div style={{
                    background: '#fafafa', border: '1px solid #d9d9d9',
                    padding: 10, borderRadius: 6, fontSize: 12, color: '#595959',
                  }}>
                    Bạn đang xem task với vai trò theo dõi. Không có hành động khả dụng.
                  </div>
                );
              })()}

              <Row gutter={6}>
                <Col span={12}>
                  <Button block size="small" icon={<Printer size={12} />}
                    onClick={() => printService.openPrintWindow(task.id)}>
                    In phiếu
                  </Button>
                </Col>
                <Col span={12}>
                  <Button block size="small" icon={<Download size={12} />}
                    onClick={() => printService.downloadPdf(task.id)}>
                    Xuất PDF
                  </Button>
                </Col>
              </Row>
            </Space>
          </div>
        </Col>
      </Row>

      {progressOpen && (
        <UpdateProgressModal
          taskId={task.id}
          currentProgress={task.progress}
          onClose={() => setProgressOpen(false)}
          onSuccess={() => { setProgressOpen(false); refetch(); }}
        />
      )}
      {submitOpen && (
        <SubmitForReviewModal
          taskId={task.id}
          task={task}
          onClose={() => setSubmitOpen(false)}
          onSuccess={() => { setSubmitOpen(false); refetch(); }}
        />
      )}
      {kpiBreakdownOpen && (
        <KpiBreakdownModal
          taskId={task.id}
          rootKpiPlan={task.rootKpiPlan ?? task.kpiRevenuePlan ?? 0}
          aggregatedActual={task.aggregatedKpiActual ?? 0}
          onClose={() => setKpiBreakdownOpen(false)}
        />
      )}
      {sendBackOpen && (
        <SendBackModal
          taskId={task.id}
          onClose={() => setSendBackOpen(false)}
          onSuccess={() => { setSendBackOpen(false); refetch(); }}
        />
      )}
      {approveOpen && (
        <ApproveTaskModal
          taskId={task.id}
          onClose={() => setApproveOpen(false)}
          onSuccess={() => {
            setApproveOpen(false);
            refetch();
            qc.invalidateQueries({ queryKey: ['task-history', id] });
          }}
        />
      )}

      {/* Edit Task Modal */}
      <Modal
        title="Chỉnh sửa thông tin task"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => {
          editForm.validateFields().then((values) => {
            // V1.0.26: derive priority cũ từ max của 2 trục
            const maxLvl = Math.max(values.priorityLevel ?? 3, values.importantLevel ?? 3);
            const derivedPriority =
              maxLvl >= 5 ? 'CRITICAL' :
              maxLvl >= 4 ? 'HIGH' :
              maxLvl >= 3 ? 'MEDIUM' : 'LOW';
            const payload = {
              ...values,
              priority: derivedPriority,
              dueDate: values.dueDate?.format('YYYY-MM-DD'),
              startDate: values.startDate?.format('YYYY-MM-DD'),
            };
            // V1.0.54: assigneeIds là single string từ Select → wrap thành array cho BE
            if (values.assigneeIds && !Array.isArray(values.assigneeIds)) {
              payload.assigneeIds = [values.assigneeIds];
            } else if (Array.isArray(values.assigneeIds) && values.assigneeIds.length > 0) {
              payload.assigneeIds = [values.assigneeIds[0]];
            }
            updateMutation.mutate(payload);
          });
        }}
        confirmLoading={updateMutation.isPending}
        okText="Lưu thay đổi"
        cancelText="Hủy"
        width={720}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="Tiêu đề" name="title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Mô tả" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Ngày bắt đầu" name="startDate">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Hạn chót" name="dueDate" rules={[{ required: true }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Loại" name="category" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'ADMIN', label: 'Hành chính' },
                  { value: 'TECHNICAL', label: 'Kỹ thuật' },
                  { value: 'MEDICAL', label: 'Y khoa' },
                  { value: 'OTHER', label: 'Khác' },
                ]} />
              </Form.Item>
            </Col>
            {/* V1.0.26: 2 trục Eisenhower thay cho Ưu tiên cũ */}
            {/* V1.0.52: Dùng StarLevelRate (sao gradient vàng→đỏ) đồng nhất */}
            <Col xs={12}>
              <Form.Item
                label={<span><span style={{ color: '#f5222d', marginRight: 4 }}>🔴</span> Mức độ Gấp</span>}
                name="priorityLevel"
                rules={[{ required: true, message: 'Chọn cấp độ gấp' }]}
              >
                <StarLevelRate tooltips={URGENT_TOOLTIPS} size={22} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                label={<span><span style={{ color: '#fa8c16', marginRight: 4 }}>🟠</span> Mức độ Quan trọng</span>}
                name="importantLevel"
                rules={[{ required: true, message: 'Chọn cấp độ quan trọng' }]}
              >
                <StarLevelRate tooltips={IMPORTANT_TOOLTIPS} size={22} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Tags" name="tags" extra="Chọn từ danh mục Nhãn / Tag. Có thể gõ thêm tag mới.">
            <Select
              mode="tags"
              placeholder="Chọn từ danh mục..."
              options={labelOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item label="Số giờ ước tính" name="estimatedHours">
            <Input type="number" min={0} step={0.5} />
          </Form.Item>
          {/* V1.0.30: KPI Doanh thu */}
          <Form.Item
            label="💰 KPI Doanh thu kế hoạch (VND)"
            name="kpiRevenuePlan"
            extra="Không bắt buộc. Nếu đặt > 0, khi đề nghị đóng task con sẽ yêu cầu nhập doanh thu thực tế."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000000}
              placeholder="VD: 100,000,000"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={((value: string | undefined) => Number(value?.replace(/\D/g, '') ?? 0)) as any}
              addonAfter="đ"
            />
          </Form.Item>
          <Form.Item name="priority" hidden><Input /></Form.Item>

          {/* V1.0.45: Cho phép sửa Khoa, Người thực hiện, Watcher, Collaborator, Informed */}
          <Form.Item label="Khoa/Phòng thực hiện" name="departmentId" rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label"
              placeholder="Chọn khoa..."
              options={(function flat(nodes: any[], pref = ''): any[] {
                const out: any[] = [];
                for (const n of nodes) {
                  // V1.0.47: Bỏ qua khoa inactive nhưng giữ khoa hiện tại của task (nếu đã ẩn sau khi gán)
                  const isInactive = n.isActive === false;
                  const isCurrent = n.id === task?.department?.id;
                  if (isInactive && !isCurrent) {
                    if (n.children?.length) out.push(...flat(n.children, pref));
                    continue;
                  }
                  const label = isInactive ? `${pref}${n.name} (đã ẩn)` : `${pref}${n.name}`;
                  out.push({ value: n.id, label });
                  if (n.children?.length) out.push(...flat(n.children, pref + '— '));
                }
                return out;
              })(orgTree)}
            />
          </Form.Item>

          <EditTaskAssigneeFields editForm={editForm} usersResp={usersResp} />
        </Form>
      </Modal>

      {/* Add Subtask Modal */}
      <Modal
        title={`Thêm task con vào "${task.title}"`}
        open={subtaskOpen}
        onCancel={() => setSubtaskOpen(false)}
        onOk={() => {
          subtaskForm.validateFields().then((values) => {
            // V1.0.26: derive priority cũ từ max của 2 trục Eisenhower
            const maxLvl = Math.max(values.priorityLevel ?? 3, values.importantLevel ?? 3);
            const derivedPriority =
              maxLvl >= 5 ? 'CRITICAL' :
              maxLvl >= 4 ? 'HIGH' :
              maxLvl >= 3 ? 'MEDIUM' : 'LOW';
            const payload = {
              ...values,
              dueDate: values.dueDate?.format('YYYY-MM-DD'),
              parentTaskId: task.id,
              departmentId: task.department?.id,
              requiresSignature: false,
              priority: derivedPriority,
            };
            // V1.0.54: assigneeIds là single string từ Select → wrap thành array cho BE
            if (values.assigneeIds && !Array.isArray(values.assigneeIds)) {
              payload.assigneeIds = [values.assigneeIds];
            } else if (Array.isArray(values.assigneeIds) && values.assigneeIds.length > 0) {
              payload.assigneeIds = [values.assigneeIds[0]];
            }
            subtaskMutation.mutate(payload);
          });
        }}
        confirmLoading={subtaskMutation.isPending}
        okText="Tạo task con"
        cancelText="Hủy"
      >
        <Form form={subtaskForm} layout="vertical">
          <Form.Item label="Tiêu đề" name="title" rules={[{ required: true }]}>
            <Input placeholder="VD: Thu thập thông tin..." />
          </Form.Item>
          <Form.Item label="Mô tả" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item label="Hạn chót" name="dueDate" rules={[{ required: true }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          {/* V1.0.26: 2 trục Eisenhower Gấp + Quan trọng */}
          {/* V1.0.52: Đồng nhất với TaskFormPage - dùng StarLevelRate (ngôi sao gradient vàng→đỏ) */}
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                label={<span><span style={{ color: '#f5222d', marginRight: 4 }}>🔴</span> Mức độ Gấp</span>}
                name="priorityLevel"
                rules={[{ required: true, message: 'Chọn cấp độ gấp' }]}
                tooltip="5 sao đỏ = Rất gấp, 1 sao vàng = Không gấp"
              >
                <StarLevelRate tooltips={URGENT_TOOLTIPS} size={22} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                label={<span><span style={{ color: '#fa8c16', marginRight: 4 }}>🟠</span> Mức độ Quan trọng</span>}
                name="importantLevel"
                rules={[{ required: true, message: 'Chọn cấp độ quan trọng' }]}
                tooltip="5 sao đỏ = Rất quan trọng, 1 sao vàng = Không quan trọng"
              >
                <StarLevelRate tooltips={IMPORTANT_TOOLTIPS} size={22} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Người thực hiện (Chính)" name="assigneeIds" rules={[{ required: true, message: 'Chọn người thực hiện' }]}>
            {/* V1.0.54: 1 người thực hiện duy nhất */}
            <Select showSearch optionFilterProp="label"
              placeholder="Chọn 1 người thực hiện"
              allowClear
              options={(usersResp?.items ?? []).map((u: any) => ({
                value: u.id, label: `${u.fullName} — ${u.department?.name ?? ''}`,
              }))}
            />
          </Form.Item>

          {/* V1.0.59: Thêm Người theo dõi, Phối hợp, Cung cấp thông tin (giống task cha) */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Người theo dõi" name="watcherIds"
                tooltip="Chỉ xem, có thể bình luận. Toàn viện.">
                <Select mode="multiple" showSearch optionFilterProp="label"
                  placeholder="Chọn người theo dõi..."
                  options={(usersResp?.items ?? []).map((u: any) => ({
                    value: u.id, label: `${u.fullName} — ${u.department?.name ?? ''}`,
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Người phối hợp" name="collaboratorIds"
                tooltip="Cùng tham gia thực hiện task con này. Toàn viện.">
                <Select mode="multiple" showSearch optionFilterProp="label"
                  placeholder="Chọn người phối hợp..."
                  options={(usersResp?.items ?? []).map((u: any) => ({
                    value: u.id, label: `${u.fullName} — ${u.department?.name ?? ''}`,
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Người cung cấp thông tin" name="informedIds"
            tooltip="Chỉ xem để biết kết quả, không tham gia. Toàn viện.">
            <Select mode="multiple" showSearch optionFilterProp="label"
              placeholder="Chọn người cung cấp thông tin..."
              options={(usersResp?.items ?? []).map((u: any) => ({
                value: u.id, label: `${u.fullName} — ${u.department?.name ?? ''}`,
              }))}
              maxTagCount="responsive"
            />
          </Form.Item>
          {/* V1.0.30: KPI Doanh thu (optional) cho task con */}
          <Form.Item
            label="💰 KPI Doanh thu kế hoạch (VND) — Không bắt buộc"
            name="kpiRevenuePlan"
            extra="Phần KPI doanh thu mà task con này dự kiến đóng góp."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000000}
              placeholder="VD: 50,000,000"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={((value: string | undefined) => Number(value?.replace(/\D/g, '') ?? 0)) as any}
              addonAfter="đ"
            />
          </Form.Item>
          <Form.Item name="category" hidden><Input /></Form.Item>
          <Form.Item name="priority" hidden><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// Helper: Map action code sang label tiếng Việt cho Lịch sử
function actionLabel(action: string): string {
  const map: Record<string, string> = {
    CREATE_TASK: 'Tạo task',
    UPDATE_TASK: 'Cập nhật thông tin',
    UPDATE_TASK_STATUS: 'Đổi trạng thái',
    UPDATE_TASK_PROGRESS: 'Cập nhật tiến độ',
    AUTO_START_TASK: 'Tự động chuyển sang Đang thực hiện',  // V1.0.57
    REQUEST_CLOSE_TASK: 'Đề nghị đóng task',  // V1.0.57
    ADD_COMMENT: 'Thêm bình luận',
    WATCH_TASK: 'Bắt đầu theo dõi',
    UNWATCH_TASK: 'Bỏ theo dõi',
    CANCEL_TASK: 'Hủy task',
    COPY_TASK: 'Sao chép task',
    DELETE_TASK: 'Xóa task',  // V1.0.57
    UPLOAD_ATTACHMENT: 'Tải lên tài liệu',
    DELETE_ATTACHMENT: 'Xóa tài liệu',
    DOWNLOAD_ATTACHMENT: 'Tải tài liệu',
  };
  return map[action] ?? action;
}

// V1.0.46: Tách thành sub-component để Form.useWatch không bị gọi trong IIFE
// (Hook trong IIFE gây React error #310 — hooks count khác giữa các render).
function EditTaskAssigneeFields({
  editForm,
  usersResp,
}: {
  editForm: any;
  usersResp: any;
}) {
  const editDept = Form.useWatch('departmentId', editForm);
  const allUsers = usersResp?.items ?? [];

  // Người thực hiện: filter theo dept
  const assigneeOpts = allUsers
    .filter((u: any) => {
      if (!editDept) return false;
      const inSec = (u.departments ?? []).some((d: any) => d.departmentId === editDept);
      const inPri = u.department?.id === editDept;
      return inSec || inPri;
    })
    .map((u: any) => ({ value: u.id, label: `${u.fullName} — ${u.position ?? ''}` }));

  // Watcher/Collab/Informed: toàn viện
  const allUserOpts = allUsers.map((u: any) => ({
    value: u.id,
    label: `${u.fullName}${u.department?.name ? ` — ${u.department.name}` : ''}`,
  }));

  return (
    <>
      <Form.Item
        label="Người thực hiện (Chính)"
        name="assigneeIds"
        rules={[{ required: true, message: 'Chọn người thực hiện' }]}
        extra={!editDept ? 'Chọn khoa trước' : `${assigneeOpts.length} người trong khoa`}
        tooltip="V1.0.54: Mỗi task chỉ có 1 người thực hiện chính."
      >
        {/* V1.0.54: 1 người thực hiện duy nhất */}
        <Select
          showSearch optionFilterProp="label"
          placeholder={editDept ? 'Chọn 1 người thực hiện...' : 'Chọn khoa trước...'}
          disabled={!editDept}
          options={assigneeOpts}
          allowClear
        />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Người theo dõi"
            name="watcherIds"
            extra="Toàn viện, không phụ thuộc khoa."
          >
            <Select
              mode="multiple" showSearch optionFilterProp="label"
              placeholder="Chọn người theo dõi..."
              options={allUserOpts}
              maxTagCount="responsive"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Người phối hợp"
            name="collaboratorIds"
            extra="Cùng tham gia, toàn viện."
          >
            <Select
              mode="multiple" showSearch optionFilterProp="label"
              placeholder="Chọn người phối hợp..."
              options={allUserOpts}
              maxTagCount="responsive"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Người cung cấp thông tin"
        name="informedIds"
        extra="Chỉ xem để biết kết quả, toàn viện."
      >
        <Select
          mode="multiple" showSearch optionFilterProp="label"
          placeholder="Chọn người cung cấp thông tin..."
          options={allUserOpts}
          maxTagCount="responsive"
        />
      </Form.Item>
    </>
  );
}
